import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck, UserX, UserPlus, Search, X, Save } from 'lucide-react';
import api from '../../services/api';
import { useSortable } from '../../lib/useSortable';
import { SortableTh } from '../../components/SortableTh';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, PERMISSION_MODULES, roleLabel, emailProblem, apiError, grantsNothing, NO_ACCESS_HINT } from './roles';

const roleBadge = (role) => {
  const cls = role === 'admin' ? 'badge-admin'
    : role === 'staff' ? 'badge-staff'
    : 'badge-cpa';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}>{roleLabel(role)}</span>;
};

const inputCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900";

function AddUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '', role: 'staff', permissions: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const togglePerm = (key) => {
    setForm(p => ({
      ...p,
      permissions: p.permissions.includes(key)
        ? p.permissions.filter(k => k !== key)
        : [...p.permissions, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const problem = emailProblem(form.email);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.post('/api/users', {
        ...form,
        email: form.email.trim().toLowerCase(),
        permissions: form.role === 'admin' ? [] : form.permissions,
      });
      onCreated(res.data);
    } catch (e) {
      setError(apiError(e, 'Failed to add user'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.35)' }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="add-user-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Add FinOps User
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-400">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="text" inputMode="email" autoComplete="off" required
              value={form.email}
              onChange={e => setF('email', e.target.value)}
              placeholder="sachin@eyvo.com"
              className={inputCls}
              data-testid="new-user-email"
            />
            <p className="text-xs text-slate-400 mt-1">
              Their exact @eyvo.com Google address. Once added, they sign in with Google as usual.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">First Name</label>
              <input
                type="text" value={form.first_name}
                onChange={e => setF('first_name', e.target.value)}
                placeholder="Sachin"
                className={inputCls}
                data-testid="new-user-first-name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Last Name</label>
              <input
                type="text" value={form.last_name}
                onChange={e => setF('last_name', e.target.value)}
                placeholder="..."
                className={inputCls}
                data-testid="new-user-last-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
            <select
              value={form.role}
              onChange={e => setF('role', e.target.value)}
              className={inputCls}
              data-testid="new-user-role"
            >
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1">{ROLES.find(r => r.value === form.role)?.description}</p>
          </div>

          {form.role !== 'admin' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">Permissions</label>
              <div className="grid grid-cols-2 gap-1.5">
                {PERMISSION_MODULES.map(mod => (
                  <label
                    key={mod.key}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-xs font-medium transition-colors ${
                      form.permissions.includes(mod.key)
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-900'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`new-user-perm-${mod.key}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={form.permissions.includes(mod.key)}
                      onChange={() => togglePerm(mod.key)}
                    />
                    {mod.label}
                  </label>
                ))}
              </div>
              {grantsNothing(form.role, form.permissions) && (
                <p className="text-xs text-amber-700 mt-2" data-testid="new-user-no-access">{NO_ACCESS_HINT}</p>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded" role="alert" data-testid="add-user-error">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50 transition-colors"
              data-testid="submit-new-user"
            >
              <Save size={14} />
              {saving ? 'Adding…' : 'Add User'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsersList() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [error,   setError]   = useState('');
  const navigate = useNavigate();
  const { user: me } = useAuth();

  const filtered = users.filter(u =>
    !search || u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted, sortKey, sortDir, onSort } = useSortable(filtered, 'full_name');

  const load = () => {
    api.get('/api/users')
      .then(r => setUsers(r.data))
      .catch(e => setError(apiError(e, 'Could not load users')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleStatus = async (user) => {
    const endpoint = user.is_active
      ? `/api/users/${user.user_id}/deactivate`
      : `/api/users/${user.user_id}/activate`;
    setError('');
    try {
      await api.post(endpoint);
      setUsers(prev => prev.map(u =>
        u.user_id === user.user_id ? { ...u, is_active: !u.is_active } : u
      ));
    } catch (e) {
      setError(apiError(e, `Could not ${user.is_active ? 'disable' : 'enable'} ${user.email}`));
    }
  };

  const handleUserCreated = (newUser) => {
    setUsers(prev => [newUser, ...prev]);
    setShowAdd(false);
  };

  return (
    <div className="p-6 fade-in" data-testid="users-list-page">
      {showAdd && (
        <AddUserModal onClose={() => setShowAdd(false)} onCreated={handleUserCreated} />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Users</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Administration — only active users with at least one permission can use Eyvo FinOps
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 transition-colors"
          data-testid="add-user-button"
        >
          <UserPlus size={15} /> Add User
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-md" role="alert" data-testid="users-error">
          {error}
        </div>
      )}

      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900"
          data-testid="users-search-input"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <SortableTh label="User"       col="full_name"      sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Role"       col="role"           sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Permissions</th>
              <SortableTh label="Status"     col="is_active"      sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Last Login" col="last_login_at"  sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <tr key={i}>
                  {[...Array(6)].map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-slate-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">No users found</td>
              </tr>
            ) : (
              sorted.map(user => (
                <tr
                  key={user.user_id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/users/${user.user_id}`)}
                  data-testid={`user-row-${user.user_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-800 text-xs font-semibold">
                            {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                            {user.last_name?.[0]?.toUpperCase() || ''}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{user.full_name || '—'}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{roleBadge(user.role)}</td>
                  <td className="px-4 py-3">
                    {user.role === 'admin' ? (
                      <span className="text-xs text-indigo-600 font-medium">All modules</span>
                    ) : grantsNothing(user.role, user.permissions) ? (
                      <span className="text-xs font-medium text-amber-700" title={NO_ACCESS_HINT}
                        data-testid={`no-access-${user.user_id}`}>No access assigned</span>
                    ) : (user.permissions || []).length === 0 ? (
                      <span className="text-xs text-slate-500">Accounts, from role</span>
                    ) : (
                      <span className="text-xs text-slate-600">
                        {(user.permissions || []).join(', ')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${user.is_active ? 'badge-active' : 'badge-inactive'}`}>
                      {user.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">
                    {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    {user.user_id !== me?.user_id && <button
                      onClick={() => toggleStatus(user)}
                      className={`p-1.5 rounded hover:bg-slate-100 ${user.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'}`}
                      title={user.is_active ? 'Disable' : 'Enable'}
                      aria-label={`${user.is_active ? 'Disable' : 'Enable'} ${user.email}`}
                      data-testid={`toggle-user-${user.user_id}`}
                    >
                      {user.is_active ? <UserX size={15} /> : <UserCheck size={15} />}
                    </button>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
